import { useMemo } from 'react'
import { ParagraphNode, TextNode } from 'lexical'
import { AutoFocusPlugin } from '@lexical/react/LexicalAutoFocusPlugin'
import {
  InitialConfigType,
  LexicalComposer
} from '@lexical/react/LexicalComposer'
import { ContentEditable } from '@lexical/react/LexicalContentEditable'
import { LexicalErrorBoundary } from '@lexical/react/LexicalErrorBoundary'
import { HistoryPlugin } from '@lexical/react/LexicalHistoryPlugin'
import { RichTextPlugin } from '@lexical/react/LexicalRichTextPlugin'
import { TabIndentationPlugin } from '@lexical/react/LexicalTabIndentationPlugin'
import { ListPlugin } from '@lexical/react/LexicalListPlugin'
import { LinkPlugin } from '@lexical/react/LexicalLinkPlugin'
import { AutoLinkNode, LinkNode } from '@lexical/link'
import { HeadingNode, QuoteNode } from '@lexical/rich-text'
import { ListItemNode, ListNode } from '@lexical/list'
import { $convertFromMarkdownString } from '@lexical/markdown'

import ToolbarPlugin from './ToolbarPlugin'
import { HeadingPlugin } from './HeadingPlugin'
import OnChangeMarkdownPlugin, { TRANSFORMERS } from './OnChangeMarkdownPlugin'
import theme from '../utils/theme'

interface Props {
  markdown: string
  placeholder: string
  readOnly?: boolean
  onChange: (markdown: string) => void
  autoFocus?: boolean
  invalid?: boolean
  name?: string
  enableTabIdentation?: boolean
}

export default function Editor(props: Props) {
  const {
    markdown,
    placeholder,
    readOnly = false,
    onChange,
    autoFocus = false,
    enableTabIdentation = false,
    invalid = false,
    name
  } = props

  const editorConfig = useMemo(() => {
    return {
      namespace: 'GuestGoodie Editor',
      nodes: [
        ParagraphNode,
        TextNode,
        HeadingNode,
        ListNode,
        ListItemNode,
        QuoteNode,
        AutoLinkNode,
        LinkNode
      ],
      onError: error => console.error(error),
      editorState: () => {
        if (!markdown) return null
        return $convertFromMarkdownString(markdown, TRANSFORMERS)
      },
      theme,
      editable: !readOnly
    } as InitialConfigType
  }, [markdown, readOnly])

  return (
    <LexicalComposer initialConfig={editorConfig}>
      <div className="relative w-full rounded-small border border-text p-1 text-regular outline-hidden focus-within:outline-solid focus-within:outline-primary">
        <ToolbarPlugin />
        <OnChangeMarkdownPlugin onChange={onChange} />
        <div className="relative max-h-[400px] overflow-y-auto bg-white p-2">
          <RichTextPlugin
            contentEditable={
              <ContentEditable
                className="outline-hidden"
                aria-placeholder={placeholder}
                aria-invalid={invalid}
                name={name}
                autoFocus={autoFocus}
                placeholder={
                  <div className="pointer-events-none absolute left-2 top-2 inline-block select-none text-regular text-text-light">
                    {placeholder}
                  </div>
                }
              />
            }
            ErrorBoundary={LexicalErrorBoundary}
          />
          <ListPlugin />
          <LinkPlugin />
          <HistoryPlugin />
          {enableTabIdentation && <TabIndentationPlugin />}
          {autoFocus && <AutoFocusPlugin />}
          <HeadingPlugin />
        </div>
      </div>
    </LexicalComposer>
  )
}
